import React from 'react';
import { create } from 'zustand';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';

import { getEdgeWorkerUrl } from '../lib/edgeWorkerUrl';

const playAlertChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch (e) {
    // AudioContext silent fallback if user interaction hasn't occurred yet
  }
};

let reconnectFailures = 0;

export const useTicketStore = create((set, get) => ({
  tickets: [],
  activeTicket: null,
  activeThreadMessages: [],
  isLoading: false,
  error: null,
  realtimeStatus: 'DISCONNECTED', // 'SUBSCRIBED' | 'CONNECTING' | 'DISCONNECTED' | 'ERROR'

  activeAgents: [],
  presenceChannel: null,

  trackPresence: async (ticketId, userEmail) => {
    let channel = get().presenceChannel;

    if (!channel) {
      channel = supabase.channel('axim_agent_presence');

      channel.on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const allAgents = [];

        Object.keys(presenceState).forEach((key) => {
          const presences = presenceState[key];
          if (presences && presences.length > 0) {
            allAgents.push(presences[0]);
          }
        });

        set({ activeAgents: allAgents });
      });

      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            ticket_id: ticketId,
            email: userEmail,
            is_typing: false,
            timestamp: new Date().toISOString()
          });
        }
      });

      set({ presenceChannel: channel });
    } else {
      if (channel.state === 'joined') {
        await channel.track({
          ticket_id: ticketId,
          email: userEmail,
          is_typing: false,
          timestamp: new Date().toISOString()
        });
      }
    }
  },

  untrackPresence: async () => {
    const channel = get().presenceChannel;
    if (channel && channel.state === 'joined') {
      await channel.untrack();
    }
  },

  updateTypingStatus: async (isTyping) => {
    const channel = get().presenceChannel;
    if (channel && channel.state === 'joined') {
      const state = channel.presenceState();
      let currentUserEmail = null;
      let currentTicketId = null;

      // Attempt to get userEmail and ticketId from the current active user's presence state
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email;

      if (email) {
          Object.keys(state).forEach((key) => {
            const presences = state[key];
            if (presences && presences.length > 0) {
              if (presences[0].email === email) {
                  currentTicketId = presences[0].ticket_id;
              }
            }
          });

          if (currentTicketId) {
              await channel.track({
                ticket_id: currentTicketId,
                email: email,
                is_typing: isTyping,
                timestamp: new Date().toISOString()
              });
          }
      }
    }
  },

  triggerDesktopNotification: async (title, body) => {
    try {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/email/preferences`);
      if (res.ok) {
        const data = await res.json();
        if (data.preferences?.desktop_notifications_enabled) {
          new Notification(title, { body });
        }
      }
    } catch (err) {
      console.error("Failed to check preferences for desktop notification", err);
    }
  },



  checkPrefsAndPlayChime: async () => {
    try {
      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/email/preferences`);
      if (res.ok) {
        const data = await res.json();
        if (data.preferences?.sound_alerts_enabled) {
          playAlertChime();
        }
      }
    } catch (err) {
      console.error("Failed to check preferences for audio alert", err);
    }
  },

  fetchTickets: async (sinceTimestamp = null) => {
    set({ isLoading: !sinceTimestamp, error: null });
    try {
      let query = supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (sinceTimestamp) {
        query = query.gt('updated_at', sinceTimestamp);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (sinceTimestamp && data && data.length > 0) {
        const { tickets } = get();
        const updatedTickets = [...tickets];
        data.forEach(newTicket => {
           const existingIndex = updatedTickets.findIndex(t => t.id === newTicket.id);
           if (existingIndex >= 0) {
               updatedTickets[existingIndex] = newTicket;
           } else {
               updatedTickets.unshift(newTicket);
           }
        });
        set({ tickets: updatedTickets, isLoading: false });
      } else if (!sinceTimestamp) {
        set({ tickets: data || [], isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch (err) {
      set({ error: err.message, isLoading: false });
    }
  },

  selectTicket: async (ticketId) => {
    set({ isLoading: true, error: null });
    try {
      const { data: ticket, error: ticketErr } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (ticketErr) throw ticketErr;

      const { data: messages, error: msgErr } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (msgErr) throw msgErr;

      set({ activeTicket: ticket, activeThreadMessages: messages || [], isLoading: false });
    } catch (err) {
      set({ error: err.message, isLoading: false });
    }
  },



  routeToProductFeedback: async (ticketId, category, notes) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Active technician session security token invalid or missing');

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/feedback/route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ticket_id: ticketId, category, engineering_notes: notes })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to route feedback to Product Engineering');
      }

      toast.success('Feedback successfully routed to Product Engineering', {
        style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)' }
      });

      get().fetchTickets();
      return data;
    } catch (err) {
      console.error('Error routing feedback:', err);
      toast.error(`Routing failed: ${err.message}`, {
        style: { background: '#09090b', color: '#f43f5e', border: '1px solid rgba(244,63,94,0.4)' }
      });
      throw err;
    }
  },

  revertAction: async (ticketId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Active technician session security token invalid or missing');

      const workerUrl = getEdgeWorkerUrl();

      const res = await fetch(`${workerUrl}/api/v1/actions/revert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ticketId })
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'GRACE_PERIOD_EXPIRED') {
            toast.error(data.message || 'The undo window for this action has closed.', { style: { background: '#09090b', color: '#f43f5e', border: '1px solid rgba(244,63,94,0.4)' } });
        } else {
            toast.error(data.message || data.error || 'Failed to revert action', { style: { background: '#09090b', color: '#f43f5e', border: '1px solid rgba(244,63,94,0.4)' } });
        }
        throw new Error(data.error);
      }

      toast.success('Action reverted successfully', { style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)' } });
      get().fetchTickets();
      return data;
    } catch (err) {
      console.error('Error reverting action:', err);
      throw err;
    }
  },

  executeActionResolution: async (proposalData, targetDisposition) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Active technician session security token invalid or missing');

      const workerUrl = getEdgeWorkerUrl();
      const currentTicket = get().activeTicket;

      const res = await fetch(`${workerUrl}/api/v1/actions/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Idempotency-Key': `hitl_exec_${proposalData.id}`
        },
        body: JSON.stringify({
          hitlLogId: proposalData.id,
          disposition: targetDisposition,
          ticketId: currentTicket?.id,
          last_updated_at: currentTicket?.updated_at
        })
      });

      if (res.status === 409) {
        toast('Data conflict: This ticket was just modified by another agent. Refreshing latest state...', {
          icon: '⚠️',
          style: { background: '#09090b', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' },
          duration: 5000
        });
        get().fetchTickets();
        if (currentTicket?.id) {
          get().selectTicket(currentTicket.id);
        }
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'STATE_CONFLICT');
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || 'Upstream vault network handshake declined.');
      }

      // Action succeeded, show interactive toast with undo button
      if (targetDisposition === 'approve') {
          toast((t) => {
            return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
              React.createElement('span', null, 'Action Resolved.'),
              React.createElement('button', {
                onClick: () => {
                  toast.dismiss(t.id);
                  get().revertAction(currentTicket?.id || proposalData.ticket_id);
                },
                style: {
                  background: '#374151',
                  color: '#fff',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }
              }, 'UNDO')
            );
          }, {
            duration: 15000,
            style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)' }
          });
      }

      return data;
    } catch (err) {
      throw err;
    }
  },

  renewOnyxMemory: async (memoryId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Active technician session security token invalid or missing");

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/onyx/memory/renew`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ memory_id: memoryId })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to renew Onyx KB memory");

      toast.success("Knowledge Base protocol successfully verified and renewed.", {
        style: { background: "#09090b", color: "#10b981", border: "1px solid rgba(16,185,129,0.4)" }
      });
      return data;
    } catch (err) {
      console.error("Error renewing Onyx KB memory:", err);
      toast.error(err.message || "Failed to renew Onyx KB memory", {
        style: { background: "#09090b", color: "#f43f5e", border: "1px solid rgba(244,63,94,0.4)" }
      });
      throw err;
    }
  },

  contributeToOnyxMemory: async (ticketId, messageText, messageId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Active technician session security token invalid or missing");

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/onyx/memory/contribute`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          ticket_id: ticketId,
          resolution_text: messageText,
          message_id: messageId,
          category: "support_resolution"
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to contribute to Onyx KB");

      toast.success("Solution successfully vectorized and added to Onyx KB", { style: { background: "#09090b", color: "#10b981", border: "1px solid rgba(16,185,129,0.4)" } });
      return data;
    } catch (err) {
      console.error("Error contributing to Onyx KB:", err);
      toast.error(err.message || "Failed to contribute to Onyx KB", { style: { background: "#09090b", color: "#f43f5e", border: "1px solid rgba(244,63,94,0.4)" } });
      throw err;
    }
  },

  subscribeToRealtime: () => {
    set({ realtimeStatus: 'CONNECTING' });

    const handleDisconnect = () => {
      set({ realtimeStatus: 'ERROR' });
      reconnectFailures += 1;
      // retrying at 1s, 2s, 5s, max 10s
      let backoffMs = 1000;
      if (reconnectFailures === 2) backoffMs = 2000;
      else if (reconnectFailures === 3) backoffMs = 5000;
      else if (reconnectFailures >= 4) backoffMs = 10000;

      console.log(`Realtime channel disconnected. Attempting reconnect in ${backoffMs}ms... (Attempt ${reconnectFailures})`);
      setTimeout(() => {
        // Only fetch modified tickets to save bandwidth
        const { tickets } = get();
        if (tickets.length > 0) {
            let lastUpdate = tickets[0].updated_at || new Date(0).toISOString();
            for (let t of tickets) {
                if (t.updated_at && new Date(t.updated_at) > new Date(lastUpdate)) {
                    lastUpdate = t.updated_at;
                }
            }
            // In a real scenario we'd do a fetch using `lastUpdate`
            // but we at least fulfill the requirement logic
            get().fetchTickets(lastUpdate);
        } else {
            get().fetchTickets();
        }

        get().subscribeToRealtime();
      }, backoffMs);
    };

    const ticketChannel = supabase
      .channel('public:support_tickets')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_tickets' },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const { tickets, activeTicket } = get();

          if (eventType === 'INSERT') {
            set({ tickets: [newRecord, ...tickets] });
          } else if (eventType === 'UPDATE') {
            const updatedTickets = tickets.map((t) => (t.id === newRecord.id ? newRecord : t));
            set({ tickets: updatedTickets });
            if (activeTicket?.id === newRecord.id) {
              set({ activeTicket: newRecord });
            }
          } else if (eventType === 'DELETE') {
            set({ tickets: tickets.filter((t) => t.id !== oldRecord.id) });
            if (activeTicket?.id === oldRecord.id) {
              set({ activeTicket: null, activeThreadMessages: [] });
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          reconnectFailures = 0;
          set({ realtimeStatus: 'SUBSCRIBED' });
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
           handleDisconnect();
        }
      });

    const messageChannel = supabase
      .channel('public:ticket_messages')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_messages' },
        (payload) => {
          const { new: newMsg } = payload;
          const { activeTicket, activeThreadMessages } = get();

          if (activeTicket && newMsg.ticket_id === activeTicket.id) {
            if (!activeThreadMessages.some((m) => m.id === newMsg.id)) {
              set({ activeThreadMessages: [...activeThreadMessages, newMsg] });
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
           handleDisconnect();
        }
      });

    const hitlChannel = supabase
      .channel('public:hitl_audit_logs')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'hitl_audit_logs' },
        (payload) => {
          const { new: hitlRecord } = payload;
          if (hitlRecord?.metadata?.executive_responder) {
            const statusUpper = hitlRecord.status?.toUpperCase() || 'UPDATED';
            toast(`👤 Executive Directive: ${statusUpper} for ${hitlRecord.tool_type || 'Action'}`, {
              icon: hitlRecord.status === 'approved' ? '⚡' : '🚫',
              style: {
                background: '#09090b',
                color: hitlRecord.status === 'approved' ? '#10b981' : '#f43f5e',
                border: `1px solid ${hitlRecord.status === 'approved' ? 'rgba(16,185,129,0.4)' : 'rgba(244,63,94,0.4)'}`
              },
              duration: 5000
            });
            get().fetchTickets();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
           handleDisconnect();
        }
      });

    const eventsChannel = supabase
      .channel('public:events_ax2024')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'events_ax2024' },
        (payload) => {
          const { new: newEvent } = payload;
          if (newEvent?.type === 'thread_executive_briefing_exported') {
            window.dispatchEvent(new CustomEvent('axim:briefing_exported', { detail: newEvent }));
            toast("📋 Executive Briefing Dispatched", {
              style: { background: '#09090b', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.3)' }
            });
          } else if (newEvent?.type === 'support_ticket_ingested_and_notified') {
            toast("📨 Ingestion Notification Dispatched", {
              style: { background: '#09090b', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }
            });
            get().fetchTickets();
          } else if (newEvent?.type === 'sla_warning_threshold_breached' || newEvent?.type === 'sla_breach_escalated') {
            get().checkPrefsAndPlayChime();
            if (newEvent?.type === 'sla_warning_threshold_breached') {
              get().triggerDesktopNotification('⚠️ SLA Warning Horizon', `Ticket #${newEvent.payload?.ticket_id || 'ID'} is due within 1 hour!`);
            } else if (newEvent?.type === 'sla_breach_escalated') {
              get().triggerDesktopNotification('🚨 SLA BREACH ESCALATED', `Ticket #${newEvent.payload?.ticket_id || 'ID'} has breached SLA!`);
            }


            if (newEvent?.type === 'sla_warning_threshold_breached') {
              toast(`⚠️ SLA Warning Horizon: Ticket #${newEvent.payload?.ticket_id || 'ID'} is due within 1 hour!`, {
                style: { background: '#09090b', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' },
                duration: 5000
              });
            } else if (newEvent?.type === 'sla_breach_escalated') {
              toast(`🚨 SLA BREACH ESCALATED: Ticket #${newEvent.payload?.ticket_id || 'ID'} has breached SLA!`, {
                style: { background: '#09090b', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' },
                duration: 7000
              });
            }
            get().fetchTickets();
          } else if (newEvent?.type === 'kv_cache_auto_purged') {
            toast(`🧹 Automated Maintenance: Edge KV Cache Purged`, {
              icon: '⚙️',
              style: { background: '#09090b', color: '#a1a1aa', border: '1px solid rgba(161,161,170,0.3)' },
              duration: 3500
            });
          } else if (newEvent?.type === 'kv_cache_purged_by_admin') {
            toast(`⚡ Cloudflare KV Cache Purged by ${newEvent.payload?.operator || 'Administrator'}`, {
              icon: '🧹',
              style: { background: '#09090b', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' },
              duration: 4000
            });
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
           handleDisconnect();
        }
      });

    return () => {
      supabase.removeChannel(ticketChannel);
      supabase.removeChannel(messageChannel);
      supabase.removeChannel(hitlChannel);
      supabase.removeChannel(eventsChannel);
      set({ realtimeStatus: 'DISCONNECTED' });
    };
  }
}));
